import {
  DataTable,
  Label,
  type LocalDataColumn,
  Meter,
  StatusBadge,
  Switch,
} from "@nymbl/nos2-rules";
import { useId, useMemo, useState } from "react";
import { usePersonGuidance } from "@/hooks/use-team";
import { PERSON_GUIDANCE_HELP } from "@/lib/column-help";
import { withCells } from "@/lib/table-columns";
import { formatHours } from "@/lib/team/metrics";
import { monthParam } from "@/lib/team/months";
import type { IsoDate } from "@/lib/time/dates";
import type { GuidanceRow } from "@/schemas/team";

export const PERSON_GUIDANCE_COLUMNS: LocalDataColumn<GuidanceRow>[] = [
  {
    key: "demand",
    label: "Demand",
    type: "text",
    accessor: (g) => g.demandName,
    searchText: (g) => (g.isBillableDemand ? "Billable" : "Internal"),
    help: PERSON_GUIDANCE_HELP.demand,
  },
  {
    key: "role",
    label: "Role",
    type: "text",
    accessor: (g) => g.roleName,
    help: PERSON_GUIDANCE_HELP.role,
  },
  {
    key: "rate",
    label: "Rate",
    type: "number",
    accessor: (g) => (g.rate ? g.rate : null),
    help: PERSON_GUIDANCE_HELP.rate,
  },
  {
    key: "planned",
    label: "Guidance vs logged",
    type: "number",
    accessor: (g) => g.planned,
    help: PERSON_GUIDANCE_HELP.planned,
  },
  {
    key: "billable",
    label: "Billable",
    type: "number",
    accessor: (g) => (g.isBillableDemand && g.billable > 0 ? g.billable : null),
    help: PERSON_GUIDANCE_HELP.billable,
  },
];

const byDemand = (a: GuidanceRow, b: GuidanceRow) => a.demandName.localeCompare(b.demandName);

function plannedLabel(g: GuidanceRow): string {
  if (g.planned > 0) return `${formatHours(g.logged)} of ${formatHours(g.planned)} hrs`;
  if (g.logged > 0) return `${formatHours(g.logged)} hrs, no guidance`;
  return "No guidance";
}

const DEMAND_TONES = { billable: "info", internal: "neutral" } as const;
const DEMAND_LABELS = { billable: "Billable", internal: "Internal" };

const COLUMNS = withCells(PERSON_GUIDANCE_COLUMNS, {
  demand: {
    className: "min-w-56",
    render: (g) => (
      <>
        <span className="font-semibold">{g.demandName}</span>{" "}
        <StatusBadge
          status={g.isBillableDemand ? "billable" : "internal"}
          tones={DEMAND_TONES}
          labels={DEMAND_LABELS}
        />
      </>
    ),
  },
  role: { render: (g) => g.roleName ?? "—" },
  rate: { render: (g) => (g.rate ? `$${formatHours(g.rate)}` : "—") },
  planned: {
    className: "min-w-40",
    render: (g) => (
      <>
        <div className="text-sm tabular-nums">{plannedLabel(g)}</div>
        {g.planned > 0 && (
          <Meter
            ratio={g.logged / g.planned}
            overTone="warning"
            hideLabel
            label={plannedLabel(g)}
          />
        )}
      </>
    ),
  },
  billable: {
    render: (g) => (g.isBillableDemand && g.billable > 0 ? formatHours(g.billable) : "—"),
  },
});

export function PersonGuidanceTab({
  personId,
  month,
  active,
}: {
  personId: string;
  month: IsoDate;
  active: boolean;
}) {
  const query = usePersonGuidance(personId, monthParam(month), active);
  const switchId = useId();
  const [showAll, setShowAll] = useState(false);
  const all = useMemo(() => [...(query.data ?? [])].sort(byDemand), [query.data]);
  const busy = useMemo(() => all.filter((g) => g.planned > 0 || g.logged > 0), [all]);
  const hidden = all.length - busy.length;

  return (
    <section aria-label="Guidance">
      <DataTable
        label="Guidance"
        search={{ placeholder: "Search demands…" }}
        exportName="person-guidance"
        columns={COLUMNS}
        rows={showAll ? all : busy}
        rowKey={(g) => g.demandPersonId}
        defaultSort="planned"
        defaultSortDir="desc"
        loading={query.isPending}
        error={query.isError ? new Error("Didn't load") : undefined}
        onRetry={() => void query.refetch()}
        empty={{ title: "No demands" }}
        controls={
          hidden > 0 && (
            <div className="flex min-h-11 items-center gap-2">
              <Switch id={switchId} checked={showAll} onCheckedChange={setShowAll} />
              <Label htmlFor={switchId} className="min-h-11 cursor-pointer whitespace-nowrap">
                Show all ({hidden} more)
              </Label>
            </div>
          )
        }
      />
    </section>
  );
}
